import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Clock, Save, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function VCRStage2({ report, onNext, onPrevious, onSave }) {
  const [formData, setFormData] = useState({
    years_driving_rm: '',
    years_professional_driving: '',
    duty_coverage: '',
    when_within_duty: '',
    precautionary_action: '',
    police_attend: '',
    police_name: '',
    police_badge_number: '',
    vehicle_ownership: '',
    vehicle_make: '',
    vehicle_reg_trailer: '',
    vehicle_reg_year: '',
    latitude: '',
    longitude: '',
    erica_number: '',
    strata_ref: '',
    police_incident_number: '',
    driver_debrief_statement: ''
  });

  const [timeRemaining, setTimeRemaining] = useState('');
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    if (report.stage2_data) {
      setFormData(report.stage2_data);
    }
  }, [report]);

  useEffect(() => {
    const interval = setInterval(() => {
      const startedAt = new Date(report.started_at);
      const twentyFourHoursLater = new Date(startedAt.getTime() + 24 * 60 * 60 * 1000);
      const now = new Date();
      const diff = twentyFourHoursLater - now;

      if (diff <= 0) {
        setIsOverdue(true);
        setTimeRemaining('OVERDUE');
      } else {
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        setTimeRemaining(`${hours}h ${minutes}m`);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [report]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/vcr/${report.id}/stage2`, formData, getAuthHeaders());
      toast.success('Progress saved');
      if (onSave) onSave();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/vcr/${report.id}/submit-stage2`,
        { ...formData, is_overdue: isOverdue },
        getAuthHeaders()
      );
      
      // Get Stage 1 data for context
      const stage1 = report.stage1_data || {};
      
      const emailSubject = isOverdue
        ? `[OVERDUE] VCR Stage 2 - ${stage1.employee_name} - ${new Date().toLocaleString()}`
        : `VCR Stage 2 - ${stage1.employee_name} - ${new Date().toLocaleString()}`;
      
      const emailBody = `Vehicle Collision Report - Stage 2 Completed (24-Hour Details)

Employee: ${stage1.employee_name}
Incident: ${stage1.incident_date} ${stage1.incident_time}

STAGE 2 DETAILS:
Years Driving RM: ${formData.years_driving_rm}
Years Professional: ${formData.years_professional_driving}

Police Attended: ${formData.police_attend}
Police Name: ${formData.police_name}
Police Badge: ${formData.police_badge_number}

Vehicle Make: ${formData.vehicle_make}
Registration: ${formData.vehicle_reg_trailer}
Reg Year: ${formData.vehicle_reg_year}

ERICA Number: ${formData.erica_number}
Strata Ref: ${formData.strata_ref}
Police Incident #: ${formData.police_incident_number}

Driver Debrief:
${formData.driver_debrief_statement}

---
Stage 2 of 4 complete. Photos pending.
VCR ID: ${report.id}`;

      const recipients = 'david.l.mills@royalmail.com,richard.ohalloran@royalmail.com,simon.ashley@royalmail.com,chris.s.palmer@royalmail.com,adam.moat@royalmail.com,james.sangwine@royalmail.com,john.b.clark1@royalmail.com,david.spittle@royalmail.com,kola.akindiya@royalmail.com,balaji.jeyaprakasam@royalmail.com,andrew.r.gardner@royalmail.com';
      const mailtoLink = `mailto:${recipients}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      
      toast.success('Stage 2 submitted - Email client opened');
      setTimeout(() => {
        if (onNext) onNext();
      }, 1000);
    } catch (error) {
      toast.error('Failed to submit stage 2');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className={isOverdue ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <span className="font-medium">Time Remaining (24 Hours)</span>
            </div>
            <span className="text-2xl font-bold text-amber-600">{timeRemaining}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-300 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-amber-600">Stage 2: 24-Hour Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Years Driving for RM</Label>
              <Input
                type="number"
                value={formData.years_driving_rm}
                onChange={(e) => handleChange('years_driving_rm', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Years Professional Driving</Label>
              <Input
                type="number"
                value={formData.years_professional_driving}
                onChange={(e) => handleChange('years_professional_driving', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Police Attend?</Label>
              <select
                className="w-full px-3 py-2 border rounded-md dark:border-slate-700"
                value={formData.police_attend}
                onChange={(e) => handleChange('police_attend', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Police Name</Label>
              <Input
                value={formData.police_name}
                onChange={(e) => handleChange('police_name', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Vehicle Make</Label>
              <Input
                value={formData.vehicle_make}
                onChange={(e) => handleChange('vehicle_make', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Registration/Trailer #</Label>
              <Input
                value={formData.vehicle_reg_trailer}
                onChange={(e) => handleChange('vehicle_reg_trailer', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Reg Year</Label>
              <Input
                value={formData.vehicle_reg_year}
                onChange={(e) => handleChange('vehicle_reg_year', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>ERICA Number</Label>
              <Input
                value={formData.erica_number}
                onChange={(e) => handleChange('erica_number', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Strata Ref #</Label>
              <Input
                value={formData.strata_ref}
                onChange={(e) => handleChange('strata_ref', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Police Incident #</Label>
              <Input
                value={formData.police_incident_number}
                onChange={(e) => handleChange('police_incident_number', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Driver Debrief/Statement *</Label>
            <Textarea
              value={formData.driver_debrief_statement}
              onChange={(e) => handleChange('driver_debrief_statement', e.target.value)}
              rows={6}
              placeholder="Detailed driver account and debrief..."
              required
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrevious}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stage 1
        </Button>
        <div className="flex space-x-2">
          <Button type="button" variant="outline" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
            <Send className="h-4 w-4 mr-2" />
            Submit Stage 2
          </Button>
        </div>
      </div>
    </form>
  );
}
